import React, {useEffect, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import BikeCard from './BikeCard';
import {getUrlImg} from '../services/firebaseStorageRepo';
import {httpsCallable} from 'firebase/functions';
import {functions} from '../firebase';
import {useTranslation} from 'react-i18next';
import {FaArrowUp, FaSearch, FaTimes} from 'react-icons/fa';

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const spin = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const ListContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 40px;
    padding: 20px;
`;

const Title = styled.h2`
    color: ${props => props.theme.colors.primary};
    text-align: center;
    margin-bottom: 20px;
`;

const SpinnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    width: 100%;
`;

const Spinner = styled.div`
    width: 50px;
    height: 50px;
    border: 5px solid ${props => props.theme.colors.secondary}50;
    border-top: 5px solid ${props => props.theme.colors.primary};
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
`;

const FilterBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;
    align-items: center;
    margin: 20px;
    animation: ${fadeIn} 0.5s ease-out forwards;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        max-width: 320px;
        margin: 20px auto;
    }
`;

const SearchWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 280px;

    @media (max-width: 768px) {
        max-width: 100%;
    }
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 8px 32px 8px 34px;
    font-size: 1rem;
    border-radius: 5px;
    border: 1px solid #ccc;
    background-color: ${props => props.theme.colors.background};
    color: #111111;
    outline: none;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
    box-sizing: border-box;

    &:focus {
        border-color: ${props => props.theme.colors.primary};
        box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}20;
    }

    &::placeholder {
        color: #555555;
        opacity: 0.8;
    }
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 10px;
    color: #555555;
    display: flex;
    align-items: center;
    pointer-events: none;
`;

const ClearSearchButton = styled.button`
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    color: #555555;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 4px;
    font-size: 0.85rem;
    transition: color 0.2s ease;

    &:hover {
        color: ${props => props.theme.colors.primary};
    }
`;

const FilterSelect = styled.select`
    padding: 8px;
    font-size: 1rem;
    border-radius: 5px;
    border: 1px solid #ccc;
    background-color: ${props => props.theme.colors.background};
    color: #111111;
    cursor: pointer;
    transition: border-color 0.3s ease;
    box-sizing: border-box;

    &:focus {
        border-color: ${props => props.theme.colors.primary};
        outline: none;
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ResetButton = styled.button`
    padding: 8px 16px;
    font-size: 1rem;
    color: ${props => props.theme.colors.background};
    background-color: ${props => props.theme.colors.primary};
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background-color: ${props => props.theme.colors.secondary};
        transform: scale(1.05);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ResultsCountBadge = styled.div`
    text-align: center;
    font-size: 0.95rem;
    color: #222222;
    margin: 0 0 15px;
    font-weight: 500;
`;

const NoResultsContainer = styled.div`
    text-align: center;
    padding: 50px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    color: #222222;
    font-size: 1.1rem;
`;

const ScrollToTopButton = styled.button`
    position: fixed;
    bottom: 20px;
    right: 20px;
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    border: none;
    border-radius: 50%;
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    animation: ${fadeIn} 0.5s ease-out forwards;
    transition: all 0.3s ease;
    z-index: 1000;

    &:hover {
        background-color: ${props => props.theme.colors.secondary};
        transform: scale(1.1);
    }

    @media (min-width: 769px) {
        display: none;
    }
`;

const BikeList = ({rentUser}) => {
    const {t} = useTranslation();
    const [items, setItems] = useState([]);
    const [imageUrls, setImageUrls] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [sizeFilter, setSizeFilter] = useState('all');
    const [sortOption, setSortOption] = useState('name-asc');

    useEffect(() => {
        const fetchBikesAndImages = async () => {
            try {
                if (!rentUser?.rentId) {
                    throw new Error(t('errorRentId', {defaultValue: 'rentId non disponibile'}));
                }

                const rentWebsiteItemsListView = httpsCallable(functions, 'rentWebsiteItemsListView');
                const response = await rentWebsiteItemsListView({rentId: rentUser.rentId});

                const bikesData = response.data.items || [];
                const imagePromises = bikesData.map(item =>
                    getUrlImg(rentUser.rentId, item.name, 'cover', item.cover)
                        .then(url => ({id: item.id, url}))
                        .catch(err => {
                            console.error(`Errore caricamento immagine per ${item.name}:`, err);
                            return {id: item.id, url: null};
                        })
                );

                const imageResults = await Promise.all(imagePromises);
                const imageUrlsMap = imageResults.reduce((acc, {id, url}) => {
                    acc[id] = url;
                    return acc;
                }, {});

                setItems(bikesData);
                setImageUrls(imageUrlsMap);
                setLoading(false);
            } catch (err) {
                console.error('Errore:', err);
                setError(t('errorLoadingBikes', {defaultValue: 'Errore nel caricamento delle bici o delle immagini'}));
                setLoading(false);
            }
        };

        if (rentUser?.rentId) {
            fetchBikesAndImages();
        }
    }, [rentUser, t]);

    // Estrai categorie uniche
    const categories = [...new Set(items.map(item => item.category).filter(Boolean))].sort();
    const sizes = ['KIDS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];

    // Filtra e ordina gli items
    const filteredItems = items
        .filter(item => {
            const matchesSearch = !searchTerm.trim() ||
                item.name?.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
                (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase().trim()));
            const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
            const matchesSize = sizeFilter === 'all' || (item.sizes && item.sizes.includes(sizeFilter));
            return matchesCategory && matchesSize && matchesSearch;
        })
        .sort((a, b) => {
            if (sortOption === 'name-asc') {
                return a.name.localeCompare(b.name);
            } else if (sortOption === 'name-desc') {
                return b.name.localeCompare(a.name);
            } else if (sortOption === 'price-asc') {
                const priceA = a.singleDayPrices ? Math.min(...a.singleDayPrices.map(p => p.price)) : Infinity;
                const priceB = b.singleDayPrices ? Math.min(...b.singleDayPrices.map(p => p.price)) : Infinity;
                return priceA - priceB;
            } else if (sortOption === 'price-desc') {
                const priceA = a.singleDayPrices ? Math.min(...a.singleDayPrices.map(p => p.price)) : Infinity;
                const priceB = b.singleDayPrices ? Math.min(...b.singleDayPrices.map(p => p.price)) : Infinity;
                return priceB - priceA;
            }
            return 0;
        });

    const resetFilters = () => {
        setSearchTerm('');
        setCategoryFilter('all');
        setSizeFilter('all');
        setSortOption('name-asc');
    };

    const scrollToTop = () => {
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    if (loading) {
        return (
            <SpinnerContainer>
                <Spinner/>
            </SpinnerContainer>
        );
    }

    if (error) return <div>{error}</div>;

    return (
        <>
            <Container>
                <Title>{t('ourBikes')}</Title>
                <FilterBar>
                    <SearchWrapper>
                        <SearchIcon>
                            <FaSearch />
                        </SearchIcon>
                        <SearchInput
                            type="text"
                            placeholder={t('searchBikesPlaceholder')}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            aria-label={t('searchBikesPlaceholder')}
                        />
                        {searchTerm && (
                            <ClearSearchButton
                                onClick={() => setSearchTerm('')}
                                aria-label={t('clearSearch')}
                                title={t('clearSearch')}
                            >
                                <FaTimes />
                            </ClearSearchButton>
                        )}
                    </SearchWrapper>
                    <FilterSelect
                        value={categoryFilter}
                        onChange={e => setCategoryFilter(e.target.value)}
                        aria-label={t('filterByCategory')}
                    >
                        <option value="all">{t('allCategories')}</option>
                        {categories.map(category => (
                            <option key={category} value={category}>
                                {category}
                            </option>
                        ))}
                    </FilterSelect>
                    <FilterSelect
                        value={sizeFilter}
                        onChange={e => setSizeFilter(e.target.value)}
                        aria-label={t('filterBySize')}
                    >
                        <option value="all">{t('allSizes')}</option>
                        {sizes.map(size => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </FilterSelect>
                    <FilterSelect
                        value={sortOption}
                        onChange={e => setSortOption(e.target.value)}
                        aria-label={t('sortBy')}
                    >
                        <option value="name-asc">{t('sortNameAsc')}</option>
                        <option value="name-desc">{t('sortNameDesc')}</option>
                        <option value="price-asc">{t('sortPriceAsc')}</option>
                        <option value="price-desc">{t('sortPriceDesc')}</option>
                    </FilterSelect>
                    <ResetButton onClick={resetFilters} aria-label={t('resetFilters')}>
                        {t('resetFilters')}
                    </ResetButton>
                </FilterBar>

                <ResultsCountBadge>
                    {t('itemsFound', {count: filteredItems.length})}
                </ResultsCountBadge>

                {filteredItems.length === 0 ? (
                    <NoResultsContainer>
                        <p>{t('noBikesFound')}</p>
                        <ResetButton onClick={resetFilters}>{t('resetFilters')}</ResetButton>
                    </NoResultsContainer>
                ) : (
                    <ListContainer>
                        {filteredItems.map((item, index) => (
                            <BikeCard
                                key={item.id}
                                rentUser={rentUser}
                                item={item}
                                index={index}
                                coverUrl={imageUrls[item.id]}
                            />
                        ))}
                    </ListContainer>
                )}
            </Container>
            <ScrollToTopButton onClick={scrollToTop} aria-label={t('scrollToTop')}>
                <FaArrowUp/>
            </ScrollToTopButton>
        </>
    );
};

export default BikeList;
